// ─── Server / Socket ─────────────────────────────────────────────────────────
export const PORT = 3000

// Where socket.io-client should connect. In production `server.js` serves
// BOTH the static SPA and the socket.io endpoint from the same origin, so
// we use window.location.origin and don't need to hardcode a Heroku URL.
//
// In local dev (vue-cli-service on :8080), point at the server.js port:
//   localhost:8080      → http://localhost:3000
//   192.168.1.5:8080    → http://192.168.1.5:3000   (phone on the same Wi-Fi)
function deriveSocketUrl() {
  if (typeof window === 'undefined') return ''   // SSR / build-time stub
  const { protocol, hostname, origin } = window.location
  const isDev = hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
  return isDev ? `${protocol}//${hostname}:${PORT}` : origin
}
export const SOCKET_URL = deriveSocketUrl()

// ─── App ─────────────────────────────────────────────────────────────────────
export const APP_NAME = 'TezTacToe'

// ─── Game layout ─────────────────────────────────────────────────────────────
export const GAME_WIDTH_FRACTION = 0.9
export const MAX_GAME_SIZE = 600
export const DEFAULT_GAME_SIZE = 500

// ─── Tezos network ───────────────────────────────────────────────────────────
// Mainnet-only. The dApp was previously toggleable between mainnet and
// shadownet (via a top-banner badge), but every game now ships its real
// mainnet KT1 — keeping the toggle just made first-time visitors land on
// a testnet by accident. Backend tooling (scripts/deploy.py, oracle
// worker, etc.) still supports both networks via --network flags.
export const NETWORK = 'mainnet'

// Master kill switch for blockchain calls. Was `false` during UI dev so we
// didn't poll dead contracts; flipped to `true` now that oracle / TXL / AD
// are originated on shadownet and `tzkt.js` already short-circuits any
// remaining `KT1XXX…` placeholder addresses.
//
// What this controls:
//   - the per-second PollingSubscribeProvider in TezTacToe
//   - the tezTacToe contractEvent subscriptions
//   - the auto setInterval polls in aceyDuecey / squaresGame
// Read-only TzKT lookups (browseNFTs, contract storage reads) are NOT
// gated — they short-circuit individually based on isPlaceholderAddress.
export const BLOCKCHAIN_ENABLED = true

// Tezos RPC node + TzKT indexer. Mainnet-only.
export const NODE_URL = 'https://mainnet.tezos.ecadinfra.com/'
export const TZKT_API_URL = 'https://api.tzkt.io'

/**
 * Beacon-compatible network config for `new BeaconWallet({ network })` and
 * `client.requestPermissions({ network })`. Plain string `type` (rather
 * than `NetworkType.X` from beacon-types) so this file doesn't pull in
 * a runtime dependency on the Beacon enum.
 */
export function getBeaconNetwork() {
  return { type: 'mainnet' }
}

/**
 * Beacon Matrix relay nodes, keyed by `Regions` enum values from
 * @airgap/beacon-types (`'europe-west'`, `'north-america-east'`, etc.).
 *
 * Why we override: @taquito/beacon-wallet@21 transitively installs
 * beacon-transport-matrix@4.5.1 whose default region map points the
 * non-EU regions at hostnames that no longer resolve:
 *   north-america-east  → beacon-node-1.beacon-server-1.papers.tech (DNS fail)
 *   north-america-west  → beacon-node-1.beacon-server-2.papers.tech (DNS fail)
 *   asia-east           → beacon-node-1.beacon-server-3.papers.tech (DNS fail)
 *   australia           → beacon-node-1.beacon-server-4.papers.tech (DNS fail)
 * The SDK probes all regions in parallel; dead probes contribute to a
 * 60s race that ultimately surfaces as "No server responded." from
 * DAppClient.js when no probe lands a healthy response.
 *
 * BeaconWallet merges this object into the SDK defaults with `{...defaults,
 * ...matrixNodes}`. Spread is by-key, so each key here REPLACES that
 * region's default entirely. Keys must match the SDK's Regions enum
 * values verbatim — a wrong key (e.g. 'EUROPE') gets merged in as a
 * junk region that the SDK iterates but never reads back, and the
 * dead defaults stay in play.
 *
 * Hosts: octez.io 1-8 are Tezos Foundation-operated and currently
 * respond 200 with CORS; we also keep a few healthy papers.tech as
 * EU redundancy.
 */
export const BEACON_MATRIX_NODES = {
  'europe-west': [
    'beacon-node-1.octez.io',
    'beacon-node-2.octez.io',
    'beacon-node-1.diamond.papers.tech',
    'beacon-node-1.sky.papers.tech',
    'beacon-node-2.sky.papers.tech',
    'beacon-node-1.hope.papers.tech',
  ],
  'north-america-east': ['beacon-node-3.octez.io'],
  'north-america-west': ['beacon-node-4.octez.io'],
  'asia-east': ['beacon-node-5.octez.io'],
  'australia': ['beacon-node-6.octez.io'],
}

/**
 * Hostnames Beacon has been seen failing on. App.vue scrubs any localStorage
 * entries containing these so the SDK can't reuse a stale cached choice.
 */
const BEACON_KNOWN_BAD_HOSTS = [
  'beacon-server-1.papers.tech',
  'beacon-server-2.papers.tech',
  'beacon-server-3.papers.tech',
  'beacon-server-4.papers.tech',
  // Single-char malformed cache entries from a prior mis-shaped
  // matrixNodes config — these get baked into beacon:matrix-* keys and
  // hang the SDK on every reload. Sweep them too.
  '"https://a/"',
  '"https://b/"',
  '"https://c/"',
  '"https://h/"',
]

/**
 * Beacon SDK caches the selected Matrix relay in localStorage under
 * `beacon:matrix-selected-node` (and a couple sibling keys). When the cached
 * choice is a dead server, the SDK ignores any matrixNodes override we pass
 * to BeaconWallet and re-tries the dead host on every connect.
 *
 * Sweep any beacon:* keys that reference a known-bad host. Idempotent —
 * safe to call on every page load.
 */
export function clearStaleBeaconStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return
  let removed = 0
  try {
    const ls = window.localStorage
    const keysToCheck = []
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i)
      if (k && k.startsWith('beacon:')) keysToCheck.push(k)
    }
    for (const key of keysToCheck) {
      const raw = ls.getItem(key) || ''
      // matches the raw hostname or the URL-encoded form
      if (BEACON_KNOWN_BAD_HOSTS.some((host) => raw.includes(host))) {
        ls.removeItem(key)
        removed++
      }
    }
    if (removed > 0) {
      console.warn(`[constants] cleared ${removed} stale Beacon storage entries`)
    }
  } catch (_e) {
    // localStorage unavailable — nothing to do
  }
}

// ─── Smart contracts ─────────────────────────────────────────────────────────
//
// Mainnet-only addresses. scripts/deploy.py still patches the *_MAINNET
// line on every mainnet origination, so this file stays in sync with what's
// live. Shadownet contracts are still maintained by the backend tooling
// for testing (deploy.py --network shadownet, oracle worker, the
// place_offui_squares.py script), but the UI no longer reads them.
//
// The unsuffixed aliases (ORACLE_CONTRACT, AD_CONTRACT_ADDRESS, …) are
// kept so components import a stable name without sprinkling _MAINNET
// everywhere.

export const ORACLE_CONTRACT_MAINNET             = 'KT1H3RJBs3SjoLyFRG3Q6LXMGtm4n5wJGa4N'
export const TXL_CONTRACT_ADDRESS_MAINNET        = 'KT1TYgt7SphtEQHLk4GySkXckhSctJww5hdj'
export const AD_CONTRACT_ADDRESS_MAINNET         = 'KT1DHjMQWua8eUzFrqtjtmjDmdeQ6YoKPXT7'
export const TTT_CONTRACT_ADDRESS_MAINNET        = 'KT1UCTsFqMZT4KN4BXQLM64bAAXvCEHJSGku'
export const OBJECT_CONTRACT_MAINNET             = 'KT1FvqJwEDWb1Gwc55Jd1jjTHRVWbYKUUpyq'
export const SQUARES_CONTRACT_ADDRESS_MAINNET    = 'KT1VQ7yMCuju7zgSdsz9VAJmDSY16BiCHNKS'
export const RNG_ORACLE_CONTRACT_ADDRESS_MAINNET = 'KT1XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'
export const REVERSI_CONTRACT_ADDRESS_MAINNET    = 'KT1XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'
export const PLINKO_CONTRACT_ADDRESS_MAINNET     = 'KT19ouAMpfbbWjRkr32kNN2yjmcM3LfoYMdk'
export const CHESS_CONTRACT_ADDRESS_MAINNET      = 'KT1XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'
export const WAR_CONTRACT_ADDRESS_MAINNET        = 'KT1XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX'

// Unsuffixed aliases — what every component imports.
export const ORACLE_CONTRACT             = ORACLE_CONTRACT_MAINNET
export const TXL_CONTRACT_ADDRESS        = TXL_CONTRACT_ADDRESS_MAINNET
export const AD_CONTRACT_ADDRESS         = AD_CONTRACT_ADDRESS_MAINNET
export const TTT_CONTRACT_ADDRESS        = TTT_CONTRACT_ADDRESS_MAINNET
export const OBJECT_CONTRACT             = OBJECT_CONTRACT_MAINNET
export const SQUARES_CONTRACT_ADDRESS    = SQUARES_CONTRACT_ADDRESS_MAINNET
export const RNG_ORACLE_CONTRACT_ADDRESS = RNG_ORACLE_CONTRACT_ADDRESS_MAINNET
export const REVERSI_CONTRACT_ADDRESS    = REVERSI_CONTRACT_ADDRESS_MAINNET
export const PLINKO_CONTRACT_ADDRESS     = PLINKO_CONTRACT_ADDRESS_MAINNET
export const CHESS_CONTRACT_ADDRESS      = CHESS_CONTRACT_ADDRESS_MAINNET
export const WAR_CONTRACT_ADDRESS        = WAR_CONTRACT_ADDRESS_MAINNET

// ─── Addresses ───────────────────────────────────────────────────────────────
// The wallet that controls admin entrypoints on every deployed game
// contract (oracle, AD, TTT, war, plinko, squares, …). This is the
// address DEPLOY_MNEMONIC derives to — same key the off-chain oracle
// worker runs with. Must match the `admin` baked into each contract's
// initial storage; if it drifts, the UI's isAdmin checks silently fail
// and the deployer can't admin from the web UI.
//
// Distinct from jamin_b's TXL-holder wallet (tz1Vq5mYKXw1dD9js26An8dXdASuzo3bfE2w).
export const ADMIN_ADDRESS = 'tz1ZU2RLW7UgY8XXz49ccKihNy86zs6TdQ8Q'

// Invite allowlist for the gated app tiers in src/apps/registry.js.
// Wallets in this list unlock every tier (beta + private) — used so the
// operator can test from multiple addresses (e.g. a hot wallet, a
// secondary wallet for the off-UI bet scripts, a phone wallet) without
// each one having to be promoted to contract-level admin. ADMIN_ADDRESS
// is always implicitly included via the spread below.
//
// Add new addresses here as you spin them up. Lowercase canonical form
// preferred but compare is case-sensitive (Tezos addresses are case-
// sensitive base58, so paste them verbatim from the wallet).
export const INVITE_ALLOWLIST = [
  ADMIN_ADDRESS,                                    // primary deployer
  'tz1VSLp8MgCRb3pUpjUGPRBuAFYrhzLq3y5V',           // secondary, derived per place_offui_squares.py SECONDARY_SALT
  'tz1QtpR6hraURtjP9V1rMMYnrqfyaicJFPWv',           // oracle worker — has touched TXL distributor + Oracle on mainnet
  'tz1Vq5mYKXw1dD9js26An8dXdASuzo3bfE2w',           // jamin_b's TXL-holder wallet — has touched AD + Plinko on mainnet
  'tz2FQEvdygAECvvrAb1WfesNwMBMfVwgf1u1',           // Kukai (secp256k1) — has touched AD on mainnet
]

// ─── Game / NFT info copy ────────────────────────────────────────────────────
export const NFT_INFO = [
  'More Information Coming Soon!',
  'All games on thextz.life generate revenue in the form of XTZ',
  'Revenue is shared evenly among active TXL holders — every NFT earns the same flat share per deposit',
  "There's only 271 of them and already over 100 unique owners",
  'They are only 2.725 XTZ on primary!',
  'The only way to cash out from the contract is by hodling the NFTs',
  'A separate smart contract tracks NFT owners and monitors for sales — holders can claim anytime',
  'Tokens held on the objkt marketplace are skipped, so their share automatically redistributes to active holders',
]

// XC-2 — `default()` semantics: the AceyDuecey contract's default
// entrypoint accepts anonymous tez and credits it to `potReserve`
// (`self.data.potReserve += sp.amount`). Sending plain tez to the KT1
// is a pot top-up, not a no-op.
export const AD_GAME_INFO = [
  'Ante up 0.4 tez to play (0.2 tez ante + 0.1 tez 2.725K holder fee + 0.1 tez v3 oracle fee). Aces are always high.',
  'Two cards are dealt. If they pair, the ante is forfeit to the pot — no refund.',
  'Otherwise, place an Acey-Duecey bet (up to 30% of the pot) that the next card lands strictly between them.',
  'Payouts are TRUE ODDS with a 5% house rake — tight spreads pay big, wide spreads pay slim:',
  '  spread 1  →  12.35× · spread 5  →  2.47× · spread 11  →  1.12×',
  'Edge (third card matches an anchor) or out-of-range third card forfeits the bet.',
  'Win, lose, or pair — every bet keeps the pot growing.',
]

export const CHESS_GAME_INFO = [
  'Standard chess. White moves first.',
  'Click a piece to select; click a destination to move.',
  'On-chain rules: piece movement, path-clear for sliders, castling, en passant, no-leaving-king-in-check.',
  'Pawn auto-promotes to a queen.',
  'No automatic checkmate detection — resign when you\'re mated, or claim by timeout if your opponent stalls.',
  'Stake matches between both players. Winner takes the pot minus the holder fee.',
]

export const REVERSI_GAME_INFO = [
  '8x8 board — black moves first.',
  'A move is legal only if it flanks at least one opponent stone in any of the 8 directions.',
  'Flanked stones flip to your color.',
  'When neither player can move (or both pass), the player with more stones takes the pot.',
  'Stake matches between both players. Winner gets the full pot minus the holder fee.',
]

export const WAR_GAME_INFO = [
  'Speed War: best-of-3 high-card showdown. Whole match settles in one oracle call.',
  'Player A creates a game with a wager (0.1 – 5 tez). Player B matches it to start.',
  'The oracle deals 6 distinct cards — three per side — in one transaction.',
  'Each round: higher rank wins that round. Whoever wins more rounds takes the pot.',
  'Rank order: A=14, K=13, Q=12, J=11, 2–10 face value. Suits are decorative.',
  'Series tied (e.g. 1–1 with a wash) → both players refunded their wager.',
  'Settles inline — no claim step. Flat 0.1 tez holder fee per side; creator can cancel an open game.',
]

// XC-2 — `default()` semantics: the Plinko contract's default
// entrypoint accepts anonymous tez and credits it to `potReserve`
// ("Anyone can top up the reserve"). Same as AD — plain tez sent to
// the KT1 is a reserve top-up.
export const PLINKO_GAME_INFO = [
  'Drop a ball through a 3D peg pyramid — 8, 12, or 16 layers deep.',
  'Each layer the ball deflects on TWO axes at once: ±X and ±Z, 50/50 on each.',
  'It lands on a grid of bins. Payout is RADIAL — the ring distance from dead centre is all that matters: centre pays sub-1×, the outer corners pay huge.',
  'Risk dials the variance: Low keeps the inner rings near 1×, High pushes the corner ring to ~17× (8 rows) up to ~575× (16 rows).',
  'Bet 0.1–10.0 tez plus a flat 0.1 tez holder fee per drop. Payout settles inline — no claim step.',
]

// 3D Plinko multipliers — RING-indexed (radially symmetric), not per-slot.
// Index = Chebyshev ring distance from centre: 0 = dead-centre bin (worst
// payout), rows/2 = outer corner ring (best). The ball lands on a
// (rows+1)×(rows+1) grid; ring = max(|finalX-rows/2|, |finalZ-rows/2|).
// Values scaled so each (rows,risk) profile pays ~97% RTP against the
// TRUE 3D ring-probability distribution (two independent Binomials).
// Must mirror scripts/plinko_seed_multipliers.py (the on-chain source
// of truth at payout time).
export const PLINKO_MULTIPLIERS = {
  8: {
    0: [0.4, 0.81, 0.89, 1.69, 4.52],
    1: [0.29, 0.5, 0.93, 2.14, 9.27],
    2: [0.12, 0.18, 0.9, 2.4, 17.43],
  },
  12: {
    0: [0.42, 0.84, 0.92, 1.17, 1.34, 2.51, 8.38],
    1: [0.21, 0.42, 0.76, 1.38, 2.77, 7.61, 22.83],
    2: [0.16, 0.16, 0.33, 1.55, 4.09, 14.71, 62.12],
  },
  16: {
    0: [0.43, 0.86, 0.94, 1.03, 1.2, 1.2, 1.72, 7.72, 13.72],
    1: [0.21, 0.34, 0.69, 1.03, 2.06, 3.43, 6.85, 28.1, 75.39],
    2: [0.12, 0.12, 0.12, 1.15, 2.3, 5.18, 14.96, 74.81, 575.44],
  },
}

// XC-2 — `default()` semantics: the TezTacToe contract's default
// entrypoint has an empty body (`pass`), so anonymous tez sent to the
// KT1 is accepted into the contract balance but credited to nothing —
// effectively discarded / unrecoverable. Unlike AD and Plinko, this is
// NOT a pot top-up. Don't send plain tez to the TTT contract.
export const GAME_INFO = [
  'TezTacToe is 3D four-in-a-row — line up four of your marks on the board before your opponent does.',
  'Sync your wallet to play. The board is up top; the wager controls and your Game Hub sit just below it.',
  'Drag the wager slider to set your stake, then hit New Game. Both players lock the same wager, plus a flat 0.1 tez holder fee per transaction.',
  'Use the Game Hub to play, join, leave, or view games. Leaving an unmatched game refunds your wager any time — the per-tx fee is non-refundable, it has already paid out to TXL holders.',
  "Once you're matched it's turn-based: when it's your turn, select a move and submit it to the blockchain, then wait for your opponent.",
  'WIN: connect four and the contract pays you the full pot minus the house cut (default 2.5%).',
  "CAT'S GAME: each side gets back their wager minus half the house cut.",
  'SURRENDER: you forfeit your stake — your opponent takes the full pot minus the house cut.',
  'The house cut and per-tx fee both flow to TXL holders — every game funds the holder pool.',
  "It's always a good time to be a 2.725K holder.",
]
